import { logPerf, measureAsync } from './performance';

export const apiRequest = async (
  url,
  { method = 'GET', body, headers = {}, isFormData = false, skipUnauthorizedRedirect = false } = {}
) => {
  const options = { method, credentials: 'include' };
  options.headers = isFormData ? headers : { 'Content-Type': 'application/json', ...headers };
  if (body !== undefined) {
    options.body = isFormData ? body : JSON.stringify(body);
  }

  const requestStartedAt = performance.now();
  const response = await fetch(url, options);
  logPerf('frontend.api.fetch', performance.now() - requestStartedAt, {
    method,
    url,
    status: response.status,
  });

  let data = {};
  const parseStartedAt = performance.now();
  try {
    data = await response.json();
  } catch (e) {
    // ignore json parse error
  } finally {
    logPerf('frontend.api.parse_json', performance.now() - parseStartedAt, {
      method,
      url,
      status: response.status,
    });
  }
  if (!response.ok) {
    if (response.status === 401 && !skipUnauthorizedRedirect) {
      window.dispatchEvent(new Event('app:unauthorized'));
      window.location.assign('/');
      return;
    }
    const error = new Error(data.message || 'Request failed');
    error.data = data;
    error.status = response.status;
    throw error;
  }
  return data;
};
export const getPublicStats = () =>
  apiRequest('/api/public/stats', { skipUnauthorizedRedirect: true });

export const getAuthStatus = () =>
  apiRequest('/api/auth/session'); // returns { authenticated: true|false }

export const getAssistantConversations = () => apiRequest('/api/assistant/conversations');
export const createAssistantConversation = () => apiRequest('/api/assistant/conversations', { method: 'POST' });
export const getAssistantConversation = (id) => apiRequest(`/api/assistant/conversations/${encodeURIComponent(id)}`);
export const deleteAssistantConversation = (id) => apiRequest(`/api/assistant/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const sendAssistantMessage = (id, payload) => apiRequest(`/api/assistant/conversations/${encodeURIComponent(id)}/turns`, { method: 'POST', body: payload });
export const getAssistantTurn = (id, turnId) => apiRequest(`/api/assistant/conversations/${encodeURIComponent(id)}/turns/${encodeURIComponent(turnId)}`);

export const getEvents = () => apiRequest('/api/events');

export const getEvent = (eventId) => apiRequest(`/api/events/${eventId}`);

export const createEvent = (event) =>
  apiRequest('/api/events', { method: 'POST', body: event });

export const replaceEvent = (eventId, event, revision) =>
  apiRequest(`/api/events/${eventId}`, {
    method: 'PUT',
    body: event,
    headers: revision == null ? {} : { 'If-Match': `"${revision}"` },
  });

export const updateEvent = (eventId, changes, revision) =>
  apiRequest(`/api/events/${eventId}`, {
    method: 'PATCH',
    body: changes,
    headers: revision == null ? {} : { 'If-Match': `"${revision}"` },
  });

export const deleteEvent = (eventId, revision) =>
  apiRequest(`/api/events/${eventId}`, {
    method: 'DELETE',
    headers: revision == null ? {} : { 'If-Match': `"${revision}"` },
  });

export const getEventShareLink = (eventId) =>
  apiRequest(`/api/events/${eventId}/share-link`);

export const createEventShareLink = (eventId) =>
  apiRequest(`/api/events/${eventId}/share-link`, { method: 'PUT' });

export const revokeEventShareLink = (eventId) =>
  apiRequest(`/api/events/${eventId}/share-link`, { method: 'DELETE' });

export const uploadEventImage = (eventId, image) => {
  const body = new FormData();
  body.append('image', image);
  return apiRequest(`/api/events/${eventId}/image`, {
    method: 'PUT',
    body,
    isFormData: true,
  });
};

export const removeEventImage = (eventId) =>
  apiRequest(`/api/events/${eventId}/image`, { method: 'DELETE' });

export const getPublicEvent = (token) =>
  apiRequest(`/api/public/events/${encodeURIComponent(token)}`, {
    skipUnauthorizedRedirect: true,
  });

export const addPublicEventToDopatize = (token) =>
  apiRequest(`/api/public/events/${encodeURIComponent(token)}/participants`, {
    method: 'POST',
    skipUnauthorizedRedirect: true,
  });

export const getPublicEventCalendarUrl = (token) =>
  `/api/public/events/${encodeURIComponent(token)}/calendar.ics`;

export const getPublicEventCalendarFile = async (token) => {
  const response = await fetch(getPublicEventCalendarUrl(token), {
    credentials: 'include',
  });
  if (!response.ok) {
    let data = {};
    try {
      data = await response.json();
    } catch {
      // The calendar endpoint can return a non-JSON proxy or server error.
    }
    const requestError = new Error(data.message || 'Unable to download this calendar event.');
    requestError.data = data;
    requestError.status = response.status;
    throw requestError;
  }

  const disposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename="([^"]+)"/i);
  return {
    blob: await response.blob(),
    filename: filenameMatch?.[1] || 'event.ics',
  };
};

export const getEventParticipants = ({ eventId, status } = {}) => {
  const params = new URLSearchParams();
  if (eventId) params.set('event_id', eventId);
  if (status) params.set('status', status);
  const query = params.toString();
  return apiRequest(`/api/event-participants${query ? `?${query}` : ''}`);
};

export const getEventParticipant = (participantId) =>
  apiRequest(`/api/event-participants/${participantId}`);

export const createEventParticipant = (participant) =>
  apiRequest('/api/event-participants', { method: 'POST', body: participant });

export const replaceEventParticipant = (participantId, participant, revision) =>
  apiRequest(`/api/event-participants/${participantId}`, {
    method: 'PUT',
    body: participant,
    headers: revision == null ? {} : { 'If-Match': `"${revision}"` },
  });

export const updateEventParticipant = (participantId, changes, revision) =>
  apiRequest(`/api/event-participants/${participantId}`, {
    method: 'PATCH',
    body: changes,
    headers: revision == null ? {} : { 'If-Match': `"${revision}"` },
  });

export const deleteEventParticipant = (participantId, revision) =>
  apiRequest(`/api/event-participants/${participantId}`, {
    method: 'DELETE',
    headers: revision == null ? {} : { 'If-Match': `"${revision}"` },
  });

export const getEventTaskLinks = ({ eventId } = {}) => {
  const params = new URLSearchParams();
  if (eventId) params.set('event_id', eventId);
  const query = params.toString();
  return apiRequest(`/api/event-task-links${query ? `?${query}` : ''}`);
};

export const getEventTaskLink = (linkId) =>
  apiRequest(`/api/event-task-links/${linkId}`);

export const createEventTaskLink = (link) =>
  apiRequest('/api/event-task-links', { method: 'POST', body: link });

export const replaceEventTaskLink = (linkId, link, revision) =>
  apiRequest(`/api/event-task-links/${linkId}`, {
    method: 'PUT',
    body: link,
    headers: revision == null ? {} : { 'If-Match': `"${revision}"` },
  });

export const updateEventTaskLink = (linkId, changes, revision) =>
  apiRequest(`/api/event-task-links/${linkId}`, {
    method: 'PATCH',
    body: changes,
    headers: revision == null ? {} : { 'If-Match': `"${revision}"` },
  });

export const deleteEventTaskLink = (linkId, revision) =>
  apiRequest(`/api/event-task-links/${linkId}`, {
    method: 'DELETE',
    headers: revision == null ? {} : { 'If-Match': `"${revision}"` },
  });

export const createEventTravelTask = (eventId, travel) =>
  apiRequest(`/api/events/${eventId}/travel-tasks`, {
    method: 'POST',
    body: travel,
  });

export const checkoutEventAssignedTasks = (eventId) =>
  apiRequest(`/api/events/${eventId}/assigned-tasks/checkout`, { method: 'POST' });

export const extendEventAssignedTasksCheckout = (eventId) =>
  apiRequest(`/api/events/${eventId}/assigned-tasks/checkout`, { method: 'PATCH' });

export const releaseEventAssignedTasksCheckout = (eventId) =>
  apiRequest(`/api/events/${eventId}/assigned-tasks/checkout`, { method: 'DELETE' });

export const deleteAuthSession = () =>
  apiRequest('/api/auth/session', {
    method: 'DELETE',
    skipUnauthorizedRedirect: true,
  });

export const getTutorialAccess = () =>
  apiRequest('/api/tutorials/access');

export const getSavedTutorials = () =>
  apiRequest('/api/tutorials');

export const createSavedTutorial = (name, tutorialPackage) =>
  apiRequest('/api/tutorials', {
    method: 'POST',
    body: { name, package: tutorialPackage },
  });

export const getSavedTutorial = (tutorialId) =>
  apiRequest(`/api/tutorials/${tutorialId}`);

export const updateSavedTutorial = (tutorialId, name, tutorialPackage, revision) =>
  apiRequest(`/api/tutorials/${tutorialId}`, {
    method: 'PUT',
    body: { name, package: tutorialPackage, revision },
  });

export const deleteSavedTutorial = (tutorialId) =>
  apiRequest(`/api/tutorials/${tutorialId}`, { method: 'DELETE' });

export const getTutorialRecipients = (tutorialId) =>
  apiRequest(`/api/tutorials/${tutorialId}/recipients`);

export const installTutorial = (tutorialId, { userIds = [], allUsers = false } = {}) =>
  apiRequest(`/api/tutorials/${tutorialId}/installations`, {
    method: 'POST',
    body: { user_ids: userIds, all_users: allUsers },
  });

export const setWelcomeTutorial = (tutorialId) =>
  apiRequest(`/api/tutorials/${tutorialId}/welcome`, { method: 'PUT' });

export const clearWelcomeTutorial = (tutorialId) =>
  apiRequest(`/api/tutorials/${tutorialId}/welcome`, { method: 'DELETE' });

export const signIn = (username, password, rememberMe = false, turnstileToken) =>
  apiRequest('/api/auth/session', {
    method: 'POST',
    body: { username, password, rememberMe, turnstileToken },
    skipUnauthorizedRedirect: true,
  });

export const getSignupInvitation = (inviteToken) =>
  apiRequest(`/api/signup-invitations/${encodeURIComponent(inviteToken)}`, {
    skipUnauthorizedRedirect: true,
  });

export const signUp = ({ inviteToken, email, username, password, turnstileToken }) =>
  apiRequest('/api/sign-up', {
    method: 'POST',
    body: {
      invite_token: inviteToken,
      email,
      username,
      password,
      turnstileToken,
    },
    skipUnauthorizedRedirect: true,
  });

export const verifyEmail = (token) =>
  apiRequest('/api/auth/email-verifications', {
    method: 'POST',
    body: { token },
    skipUnauthorizedRedirect: true,
  });

const serializeDateParam = (value) => (value instanceof Date ? value.toISOString() : value);

const taskTreeQueryParams = (filters = 'all') => {
  const normalized = typeof filters === 'string' ? { assignment: filters } : (filters || {});
  const assignment = normalized.assignment || 'all';
  const params = new URLSearchParams({ assignment });
  const completedExclude = normalized.exclude?.completed;

  if (completedExclude?.after) {
    params.set('exclude_completed_after', serializeDateParam(completedExclude.after));
  }
  if (completedExclude?.before) {
    params.set('exclude_completed_before', serializeDateParam(completedExclude.before));
  }

  return { assignment, params };
};

export const fetchAllTasks = (filters = 'all') => {
  const { assignment, params } = taskTreeQueryParams(filters);
  return measureAsync(
    'frontend.tasks.fetch_all',
    () => apiRequest(`/api/tasks?${params.toString()}`),
    { assignment },
  );
};

export const getUserStats = () => apiRequest('/api/users/me/stats');

export const uploadTasks = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiRequest('/api/upload', { method: 'POST', body: formData, isFormData: true });
};

export const createTaskTree = (tree) =>
  apiRequest('/api/task-trees', { method: 'POST', body: tree });

export const getTaskTree = (taskId) =>
  apiRequest(`/api/task-trees/${taskId}`);

export const replaceTaskTree = (taskId, tree) =>
  measureAsync('frontend.task_trees.replace', () => apiRequest(`/api/task-trees/${taskId}`, { method: 'PUT', body: tree }), { taskId });

export const deleteTaskTree = (taskId) =>
  apiRequest(`/api/task-trees/${taskId}`, { method: 'DELETE' });

export const assignTask = (taskId,body={assigned:true}) =>
  apiRequest(`/api/tasks/${taskId}/assignments`, { method: 'POST', body: body });

export const generateTask = (formData, operationKey) =>
  console.log('Generating task with prompt:', formData) ||
  apiRequest('/api/tasks/generations', {
    method: 'POST',
    body: { ...formData, operation_key: operationKey },
  });

export const getCategories = () =>
  apiRequest('/api/categories');

export const getCategory = (categoryId) =>
  apiRequest(`/api/categories/${categoryId}`);

export const createCategory = (category) =>
  apiRequest('/api/categories', { method: 'POST', body: category });

export const updateCategory = (categoryId, category) =>
  apiRequest(`/api/categories/${categoryId}`, { method: 'PATCH', body: category });

export const deleteCategory = (categoryId) =>
  apiRequest(`/api/categories/${categoryId}`, { method: 'DELETE' });

export const updateTaskCategory = (taskId, categoryId) =>
  apiRequest(`/api/tasks/${taskId}/category`, {
    method: 'PATCH',
    body: { category_id: categoryId },
  });

export const saveSchedules = (schedules) =>
  apiRequest('/api/schedules', { method: 'POST', body: schedules });

export const getSchedules = () =>
  apiRequest('/api/schedules');

export const getAssignedTasks = (taskId) =>
  apiRequest(`/api/tasks/${taskId}/assigned-tasks`);

export const checkoutAssignedTasks = (taskId) =>
  apiRequest(`/api/tasks/${taskId}/assigned-tasks/checkout`, { method: 'POST' });

export const extendAssignedTasksCheckout = (taskId) =>
  apiRequest(`/api/tasks/${taskId}/assigned-tasks/checkout`, { method: 'PATCH' });

export const releaseAssignedTasksCheckout = (taskId) =>
  apiRequest(`/api/tasks/${taskId}/assigned-tasks/checkout`, { method: 'DELETE' });

export const resetAssignedTaskOrder = (taskId) =>
  apiRequest(`/api/tasks/${taskId}/assigned-tasks/reset-order`, { method: 'POST' });

export const getAllAssignedTasks = (page = 1, limit = 100, filters = {}) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    timezone: userTimezone,
  });
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, item));
    } else {
      params.set(key, value);
    }
  });
  return apiRequest(`/api/assigned-tasks?${params.toString()}`);
};

export const updateAssignedTask = (assignedTaskId, data) =>
  measureAsync('frontend.assigned_tasks.update', () => apiRequest(`/api/assigned-tasks/${assignedTaskId}`, { method: 'PATCH', body: data }), { assignedTaskId });

export const createTaskEvents = (taskId, eventTypes, assignedTaskId = null) =>
  apiRequest(`/api/tasks/${taskId}/events`, {
    method: 'POST',
    body: {
      event_types: eventTypes,
      ...(assignedTaskId ? { assigned_task_id: assignedTaskId } : {}),
    },
  });

export const updateTaskPoints = (assignedTaskId, assignedTask, type = 'standard') =>
  apiRequest(`/api/assigned-tasks/${assignedTaskId}/points`, { method: 'POST', body: { type, assigned_task: assignedTask } });

export const updateSchedule = () => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return apiRequest(`/api/schedules/refresh?timezone=${encodeURIComponent(userTimezone)}`, {
    method: 'POST'
  });
};

export const getUserInterests = () =>
  apiRequest('/api/users/me/interests');

export const updateUserInterests = (interests) =>
  apiRequest('/api/users/me/interests', { method: 'PATCH', body: { interests } });

export const getUserProfile = () =>
  apiRequest('/api/users/me');

export const getUserScore = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return apiRequest(`/api/users/me/score?${new URLSearchParams({ timezone })}`);
};

export const getLeaderboard = (period = 'daily') => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return apiRequest(`/api/leaderboard?${new URLSearchParams({ period, timezone })}`);
};

export const getBillingPlans = () =>
  apiRequest('/api/billing/plans');

export const getBillingStatus = () =>
  apiRequest('/api/billing/status');

export const createBillingSubscription = (planKey, operationKey) =>
  apiRequest('/api/billing/subscriptions', {
    method: 'POST',
    body: {
      plan_key: planKey,
      operation_key: operationKey,
    },
  });

export const recoverBillingConfirmation = () =>
  apiRequest('/api/billing/subscriptions/current/confirmation', {
    method: 'POST',
  });

export const verifyBillingSubscription = () =>
  apiRequest('/api/billing/subscriptions/current/verification', {
    method: 'POST',
  });

export const scheduleSubscriptionCancellation = () =>
  apiRequest('/api/billing/subscriptions/current/cancellation', {
    method: 'POST',
  });

export const keepSubscriptionActive = () =>
  apiRequest('/api/billing/subscriptions/current/cancellation', {
    method: 'DELETE',
  });

export const getRewardStatus = () =>
  apiRequest('/api/images');

const galleryQuery = ({ limit = 12, cursor = null } = {}) => {
  const params = new URLSearchParams({ limit: limit.toString() });
  if (cursor) params.set('cursor', cursor);
  return params.toString();
};

export const getRewardGallery = (options) =>
  apiRequest(`/api/images/gallery?${galleryQuery(options)}`);

export const getUnseenRewardImages = () =>
  apiRequest('/api/images/unseen');

export const processRewardJob = () =>
  apiRequest('/api/images/reward-jobs/process', { method: 'POST' });

export const acknowledgeRewardQuotaNotices = (jobIds) =>
  apiRequest('/api/images/reward-jobs/quota-notices/acknowledge', {
    method: 'POST',
    body: { job_ids: jobIds },
  });

export const updateUsername = (username) =>
  apiRequest('/api/users/me/username', { method: 'PATCH', body: { username } });

export const updateEmail = (email) =>
  apiRequest('/api/users/me/email', { method: 'PATCH', body: { email } });

export const resendEmailVerification = () =>
  apiRequest('/api/users/me/email-verification', { method: 'POST' });

export const getSignupInviteStatus = () =>
  apiRequest('/api/users/me/signup-invite');

export const createImageSignupInvite = (imageId) =>
  apiRequest(`/api/images/${imageId}/signup-invite`, { method: 'POST' });

export const setProfileImage = (imageId) =>
  apiRequest('/api/users/me/profile-image', { method: 'PATCH', body: { image_id: imageId } });

export const clearProfileImage = () =>
  apiRequest('/api/users/me/profile-image', { method: 'PATCH', body: { image_id: null } });

export const updatePassword = (currentPassword, newPassword) =>
  apiRequest('/api/users/me/password', {
    method: 'PATCH',
    body: { currentPassword, newPassword },
  });

export const getNotificationPreferences = () =>
  apiRequest('/api/notifications/preferences');

export const updateNotificationPreference = (notificationType, enabled) =>
  apiRequest('/api/notifications/preferences', {
    method: 'PATCH',
    body: {
      notification_type: notificationType,
      channel: 'push',
      enabled,
    },
  });

export const updateDueDateReminderPreference = (enabled) =>
  updateNotificationPreference('due_date_reminder', enabled);

export const registerPushInstallation = (installationId, platform) =>
  apiRequest('/api/notifications/push-registrations', {
    method: 'POST',
    body: {
      installation_id: installationId,
      platform,
    },
  });

export const disablePushInstallation = (installationId) =>
  apiRequest('/api/notifications/push-registrations/current', {
    method: 'DELETE',
    body: { installation_id: installationId },
  });

export const getNotificationDisplay = (notificationId) =>
  apiRequest(`/api/notifications/${notificationId}/display`, {
    skipUnauthorizedRedirect: true,
  });

export const getFriends = () =>
  apiRequest('/api/friends');

export const getFriendGallery = (friendUserId, options) =>
  apiRequest(`/api/friends/${friendUserId}/gallery?${galleryQuery(options)}`);

export const getFriendGalleryByUsername = (username, options) =>
  apiRequest(`/api/friends/by-username/${encodeURIComponent(username)}/gallery?${galleryQuery(options)}`);

export const getFriendRequests = () =>
  apiRequest('/api/friend-requests');

export const sendFriendRequest = (username) =>
  apiRequest('/api/friend-requests', { method: 'POST', body: { username } });

export const respondToFriendRequest = (requestId, status) =>
  apiRequest(`/api/friend-requests/${requestId}`, { method: 'PATCH', body: { status } });

export const removeFriend = (friendUserId) =>
  apiRequest(`/api/friends/${friendUserId}`, { method: 'DELETE' });

export const submitWaitlist = async (email, turnstileToken) => {
  try {
    await apiRequest('/api/subscribe', { method: 'POST', body: { email, turnstileToken } });
    return { status: 'success', message: 'Thanks for subscribing!' };
  } catch (error) {
    if (error.status === 409) {
      return { status: 'warning', message: error.data?.message };
    }
    return { status: 'error', message: error.data?.message || 'Something went wrong.' };
  }
};
